import { FilterSpec } from '@numistic/contracts'
import {
  DataError,
  useCreateNode,
  useCreateSavedFilter,
  useDataClient,
  useMeta,
  useRemoveNode,
  useTree,
  useUpdateNode,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Card, Checkbox, Input } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { z } from 'zod'

import { MutationErrors } from '../components/mutation-errors.js'
import { useUrlSearchText } from '../lib/url-search-text.js'
import { SharePanel } from '../collections/share-panel.js'
import { SpecimenList } from '../collections/specimen-list.js'
import { TreeView, type TreeNode } from '../collections/tree-view.js'
import { useErrorText } from '../lib/errors.js'
import { requireSession } from '../lib/guards.js'
import { FilterBuilder } from '../meta/filter-builder.js'
import { rootRoute } from './root.js'

export const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c',
  validateSearch: z.object({
    collection: z.string().optional().catch(undefined),
    children: z.boolean().optional().catch(undefined),
    q: z.coerce.string().optional().catch(undefined),
    filter: FilterSpec.optional().catch(undefined),
  }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: CollectionsPage,
})

function CollectionsPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const search = collectionsRoute.useSearch()
  const navigate = useNavigate({ from: '/c' })
  const meta = useMeta().data
  const tree = useTree('collections')
  const create = useCreateNode('collections')
  const update = useUpdateNode('collections')
  const remove = useRemoveNode('collections')
  const saveView = useCreateSavedFilter()
  const client = useDataClient()
  const setQ = useCallback(
    (q: string | undefined) => void navigate({ search: (s) => ({ ...s, q }), replace: true }),
    [navigate],
  )
  const [text, setText] = useUrlSearchText(search.q, setQ)
  const [showFilters, setShowFilters] = useState(Boolean(search.filter))
  const [sharing, setSharing] = useState(false)
  const [treeOpen, setTreeOpen] = useState(false)
  const nodes = (tree.data ?? []) as TreeNode[]
  const selected = nodes.find((n) => n.id === search.collection)

  const addCollection = (parentId: string | null) => {
    const name = window.prompt(t('collections.namePrompt'))?.trim()
    if (name)
      create.mutate(
        { name, parentId },
        { onSuccess: (n) => void navigate({ search: (s) => ({ ...s, collection: n.id }) }) },
      )
  }
  const rename = (node: TreeNode) => {
    const name = window.prompt(t('collections.namePrompt'), node.name)?.trim()
    const full = (tree.data ?? []).find((n) => n.id === node.id) as
      (TreeNode & { version: number }) | undefined
    if (name && name !== node.name && full)
      update.mutate({ id: node.id, version: full.version, name })
  }
  const move = (id: string, parentId: string | null) => {
    const node = (tree.data ?? []).find((n) => n.id === id) as
      (TreeNode & { version: number }) | undefined
    if (node) update.mutate({ id, version: node.version, parentId })
  }
  const error = create.error ?? update.error ?? remove.error

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
      <aside className="grid content-start gap-3">
        {/* На телефоне дерево свёрнуто в одну кнопку, чтобы не закрывать список экземпляров. */}
        <Button
          variant="outline"
          className="justify-between lg:hidden"
          aria-expanded={treeOpen}
          aria-controls="collections-tree"
          onClick={() => setTreeOpen((v) => !v)}
        >
          <span className="truncate">{selected?.name ?? t('collections.all')}</span>
          <span aria-hidden>{treeOpen ? '▴' : '▾'}</span>
        </Button>
        <div id="collections-tree" className={treeOpen ? 'grid gap-3' : 'hidden gap-3 lg:grid'}>
          <div className="flex items-center gap-2">
            <h1 className="mr-auto text-lg font-semibold">{t('collections.title')}</h1>
            <Button size="sm" variant="outline" onClick={() => addCollection(null)}>
              {t('collections.newRoot')}
            </Button>
          </div>
          {error ? (
            <Alert variant="destructive">
              {error instanceof DataError && error.code === 'CYCLE'
                ? t('collections.cycle')
                : errorText.request(error)}
            </Alert>
          ) : null}
          <TreeView
            nodes={nodes}
            rootLabel={t('collections.all')}
            selectedId={search.collection ?? null}
            onSelect={(id) => {
              setTreeOpen(false)
              void navigate({ search: (s) => ({ ...s, collection: id ?? undefined }) })
            }}
            onMove={move}
            actions={(node) => (
              <>
                <button
                  type="button"
                  className="px-1.5 py-1 text-xs hover:text-primary"
                  title={t('collections.newChild')}
                  aria-label={`${t('collections.newChild')}: ${node.name}`}
                  onClick={() => addCollection(node.id)}
                >
                  +
                </button>
                {!node.isInbox ? (
                  <button
                    type="button"
                    className="px-1.5 py-1 text-xs hover:text-primary"
                    title={t('collections.rename')}
                    aria-label={`${t('collections.rename')}: ${node.name}`}
                    onClick={() => rename(node)}
                  >
                    ✎
                  </button>
                ) : null}
                {!node.isInbox ? (
                  <button
                    type="button"
                    className="px-1.5 py-1 text-xs hover:text-destructive"
                    title={t('collections.delete')}
                    aria-label={`${t('collections.delete')}: ${node.name}`}
                    onClick={() => {
                      if (window.confirm(t('collections.deleteConfirm')))
                        remove.mutate(node.id, {
                          // Удалённая коллекция была открыта — её id в адресе больше ничего не значит.
                          onSuccess: () => {
                            if (search.collection === node.id)
                              void navigate({
                                search: (s) => ({ ...s, collection: node.parentId ?? undefined }),
                              })
                          },
                        })
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </>
            )}
          />
          <p className="text-xs text-muted-foreground">
            {t('collections.dragHint')}{' '}
            <Link
              to="/settings/collections"
              search={{}}
              className="text-primary underline underline-offset-4 hover:decoration-2"
            >
              {t('collections.manage')}
            </Link>
          </p>
          <nav className="grid gap-1 border-t pt-3 text-sm">
            <Link to="/wanted" className="text-muted-foreground hover:text-foreground">
              {t('collections.wanted')}
            </Link>
            <Link to="/for-sale" className="text-muted-foreground hover:text-foreground">
              {t('collections.forSale')}
            </Link>
          </nav>
        </div>
      </aside>
      <section className="grid content-start gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="mr-auto text-xl font-semibold">
            {selected?.name ?? t('collections.all')}
          </h2>
          {selected ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={Boolean(search.children)}
                onChange={(e) =>
                  navigate({ search: (s) => ({ ...s, children: e.target.checked || undefined }) })
                }
              />
              {t('collections.includeChildren')}
            </label>
          ) : null}
          {selected ? (
            <Button
              variant="outline"
              size="sm"
              aria-expanded={sharing}
              onClick={() => setSharing((v) => !v)}
            >
              {t('share.open')}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((v) => !v)}
          >
            {t('catalog.filters')}
          </Button>
          <Button asChild variant="outline" size="sm">
            <a
              download
              href={client.io.exportUrl('csv', {
                collectionId: search.collection,
                includeChildren: search.children ? 'true' : undefined,
                filter: search.filter?.and.length ? JSON.stringify(search.filter) : undefined,
              })}
            >
              {t('io.exportView')}
            </a>
          </Button>
          <Button asChild size="sm">
            <Link to="/s/new" search={{ collection: search.collection }}>
              {t('collections.addSpecimen')}
            </Link>
          </Button>
        </div>
        {sharing && selected ? (
          <SharePanel key={selected.id} collectionId={selected.id} name={selected.name} />
        ) : null}
        <Input
          type="search"
          aria-label={t('common.search')}
          placeholder={t('catalog.searchPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {showFilters && meta ? (
          <Card className="grid gap-3 p-4">
            <FilterBuilder
              meta={meta}
              level="specimen"
              value={search.filter ?? { and: [] }}
              onChange={(spec) =>
                navigate({
                  search: (s) => ({ ...s, filter: spec.and.length ? spec : undefined }),
                  replace: true,
                })
              }
            />
            {search.filter?.and.length ? (
              <Button
                variant="outline"
                size="sm"
                className="justify-self-start"
                onClick={() => {
                  const name = window.prompt(t('collections.saveViewPrompt'))?.trim()
                  if (name && search.filter)
                    saveView.mutate({
                      name,
                      filter: search.filter,
                      params: { collectionId: search.collection, includeChildren: search.children },
                    })
                }}
              >
                {t('collections.saveView')}
              </Button>
            ) : null}
            <MutationErrors errors={[saveView.error]} />
          </Card>
        ) : null}
        <SpecimenList
          query={{
            collectionId: search.collection,
            includeChildren: search.children ? 'true' : undefined,
            q: search.q,
            filter: search.filter,
          }}
        />
      </section>
    </div>
  )
}
